import React, { useEffect, useMemo, useRef, useState } from "react";
import Sidebar from "../components/Sidebar";
import axios from "axios";
import "../assets/css/results-view.css";

export default function StudentResultsView() {
  const printRef = useRef(null);

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("");
  const [selectedSemester, setSelectedSemester] = useState("");
  const [studentInfo, setStudentInfo] = useState(null);

  const token = localStorage.getItem("token");
  const matricNo = localStorage.getItem("matricNo");

  const isProduction = process.env.NODE_ENV === "production";
  const API = isProduction
    ? process.env.REACT_APP_API_BASE_URL || "https://schools.luth.gov.ng"
    : "http://localhost:3003";

  const gradeFromScore = (score) => {
    const n = Number(score);
    if (Number.isNaN(n)) return { grade: "N/A", points: 0 };
    if (n >= 70) return { grade: "A", points: 4 };
    if (n >= 60) return { grade: "B", points: 3 };
    if (n >= 50) return { grade: "C", points: 2 };
    if (n >= 40) return { grade: "D", points: 1 };
    return { grade: "F", points: 0 };
  };

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const headers = token
          ? { Authorization: `Bearer ${token}` }
          : undefined;

        const [resResults, resBio] = await Promise.all([
          axios.get(`${API}/api/results/view`, {
            headers,
            params: { matricNo },
          }),
          axios.get(`${API}/api/biodata/${matricNo}`, { headers }),
        ]);

        const raw = Array.isArray(resResults.data)
          ? resResults.data
          : resResults.data?.recordset || [];

        if (!cancelled) {
          setResults(raw);
          setStudentInfo(resBio.data || null);
        }
      } catch (err) {
        console.error("Failed to load results or biodata:", err);
        if (!cancelled) setError("Failed to load results.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [API, matricNo, token]);

  const uniqueLevels = useMemo(() => {
    return [...new Set(results.map((r) => r.CourseLevel).filter(Boolean))].sort(
      (a, b) => Number(a) - Number(b),
    );
  }, [results]);

  const uniqueSemesters = useMemo(() => {
    const rows = selectedLevel
      ? results.filter((r) => String(r.CourseLevel) === String(selectedLevel))
      : results;

    return [
      ...new Set(rows.map((r) => r.CourseSemester).filter(Boolean)),
    ].sort();
  }, [results, selectedLevel]);

  const filteredResults = useMemo(() => {
    return results
      .filter((r) =>
        selectedLevel ? String(r.CourseLevel) === String(selectedLevel) : true,
      )
      .filter((r) =>
        selectedSemester
          ? String(r.CourseSemester) === String(selectedSemester)
          : true,
      )
      .map((r) => {
        const score = r.Score == null ? null : Number(r.Score);
        const computed = gradeFromScore(score);
        const grade = r.Grade || computed.grade;
        const points = computed.points;
        const units = Number(r.CreditUnits ?? 0) || 0;

        return {
          ...r,
          Score: score,
          Grade: grade,
          Points: points,
          Units: units,
        };
      });
  }, [results, selectedLevel, selectedSemester]);

  const { semesterGPA, semesterTotalUnits } = useMemo(() => {
    let totalUnits = 0;
    let totalGradePoints = 0;

    for (const r of filteredResults) {
      const u = Number(r.Units || 0);
      const p = Number(r.Points || 0);
      if (!Number.isFinite(u) || u <= 0) continue;
      totalUnits += u;
      totalGradePoints += p * u;
    }

    const gpa = totalUnits > 0 ? totalGradePoints / totalUnits : 0;

    return {
      semesterGPA: Number.isFinite(gpa) ? Number(gpa.toFixed(2)) : 0,
      semesterTotalUnits: totalUnits,
    };
  }, [filteredResults]);

  const { cgpa, cgpaTotalUnits } = useMemo(() => {
    let totalUnits = 0;
    let totalGradePoints = 0;

    for (const r of results) {
      const score = r.Score == null ? null : Number(r.Score);
      const computed = gradeFromScore(score);
      const points = r.Points != null ? Number(r.Points) : computed.points;
      const units = Number(r.CreditUnits ?? 0) || 0;

      if (!Number.isFinite(units) || units <= 0) continue;

      totalUnits += units;
      totalGradePoints += points * units;
    }

    const val = totalUnits > 0 ? totalGradePoints / totalUnits : 0;

    return { cgpa: Number(val.toFixed(2)), cgpaTotalUnits: totalUnits };
  }, [results]);

  const isProvisional = useMemo(() => {
    if (!filteredResults.length) return false;
    return filteredResults.some(
      (r) =>
        r.Approved !== true &&
        r.Finalized !== true &&
        String(r.Status ?? "").toLowerCase() !== "final",
    );
  }, [filteredResults]);

  const handlePrint = () => {
    const printContents = printRef.current?.innerHTML || "";
    const win = window.open("", "", "height=700,width=900");
    if (!win) return;

    win.document.write("<html><head><title>Print Results</title>");
    win.document.write(`
      <style>
        @page {
          size: A4;
          margin: 18mm;
        }
        body {
          font-family: 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif;
          margin: 0;
          font-size: 12px;
          color: #111;
        }
        .print-area {
          position: relative;
          min-height: 100vh;
        }
        .watermark {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(-30deg);
          font-size: 64px;
          font-weight: 800;
          letter-spacing: 3px;
          color: rgba(200, 0, 0, 0.12);
          z-index: 0;
          white-space: nowrap;
          pointer-events: none;
          user-select: none;
        }
        .print-header,
        .print-meta,
        .provisional-banner,
        .results-table,
        .footer-summary {
          position: relative;
          z-index: 1;
        }
        .print-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        .print-title {
          text-align: center;
          margin-bottom: 0.2rem;
        }
        .print-title h2 {
          margin: 0;
          font-size: 18px;
        }
        .print-subtitle h3 {
          margin: 0;
          font-size: 15px;
          color: #333;
        }
        .print-meta {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          margin: 0.75rem 0 1rem;
        }
        .meta-box {
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 10px 12px;
          min-width: 180px;
        }
        .provisional-banner {
          margin: 0 0 12px;
          padding: 8px 12px;
          border: 1px solid #f0c36d;
          background: #fff7e6;
          color: #8a6d3b;
          font-weight: 700;
          border-radius: 8px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 1rem;
        }
        th, td {
          border: 1px solid #ddd;
          padding: 6px 8px;
          vertical-align: top;
        }
        th {
          background: #f2f2f2;
          text-align: left;
        }
        .footer-summary {
          margin-top: 12px;
        }
        .footer-summary div {
          margin-bottom: 4px;
        }
      </style>
    `);
    win.document.write("</head><body>");
    win.document.write(`<div class="print-area">${printContents}</div>`);
    win.document.write("</body></html>");
    win.document.close();

    const triggerPrint = () => {
      win.focus();
      win.print();
      win.close();
    };

    if (win.document.readyState === "complete") {
      triggerPrint();
    } else {
      win.onload = triggerPrint;
    }
  };

  if (loading) return <p>Loading…</p>;

  return (
    <div className="result-page" style={{ marginTop: "-130px" }}>
      <Sidebar />
      <div className="result-main" style={{ marginTop: "-10px" }}>
        <main className="results-view-page">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <h1>Your Results</h1>

            <button
              onClick={handlePrint}
              style={{
                padding: "10px 14px",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                fontWeight: 700,
                background: "#111827",
                color: "#fff",
              }}
            >
              Print Result
            </button>
          </div>

          {error && <p className="error">{error}</p>}

          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              flexWrap: "wrap",
              marginBottom: 12,
            }}
          >
            <label>
              Level:&nbsp;
              <select
                value={selectedLevel}
                onChange={(e) => {
                  setSelectedLevel(e.target.value);
                  setSelectedSemester("");
                }}
              >
                <option value="">All Levels</option>
                {uniqueLevels.map((lvl) => (
                  <option key={lvl} value={lvl}>
                    {lvl}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Semester:&nbsp;
              <select
                value={selectedSemester}
                onChange={(e) => setSelectedSemester(e.target.value)}
              >
                <option value="">All Semesters</option>
                {uniqueSemesters.map((sem) => (
                  <option key={sem} value={sem}>
                    {sem}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div
            style={{
              display: "flex",
              gap: 12,
              marginBottom: 12,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                minWidth: 140,
                padding: "10px 14px",
                borderRadius: 12,
                background: "linear-gradient(135deg,#3b82f6,#60a5fa)",
                color: "#fff",
                fontWeight: 700,
              }}
            >
              CGPA
              <br />
              <span style={{ fontSize: 18 }}>
                {Number.isFinite(cgpa) ? cgpa.toFixed(2) : "0.00"}
              </span>
            </div>

            <div
              style={{
                minWidth: 160,
                padding: "10px 14px",
                borderRadius: 12,
                background: "linear-gradient(135deg,#10b981,#34d399)",
                color: "#fff",
                fontWeight: 700,
              }}
            >
              Semester GPA
              <br />
              <span style={{ fontSize: 18 }}>
                {Number.isFinite(semesterGPA) ? semesterGPA.toFixed(2) : "0.00"}
              </span>
            </div>

            <div
              style={{
                minWidth: 140,
                padding: "10px 14px",
                borderRadius: 12,
                background: "linear-gradient(135deg,#f59e0b,#fbbf24)",
                color: "#111",
                fontWeight: 700,
              }}
            >
              Semester Units
              <br />
              <span style={{ fontSize: 16 }}>{semesterTotalUnits || 0}</span>
            </div>
          </div>

          {isProvisional && (
            <div
              style={{ marginBottom: 12, color: "#8a6d3b", fontWeight: 600 }}
            >
              <span
                style={{
                  background: "#fff3cd",
                  padding: "8px 12px",
                  borderRadius: 6,
                }}
              >
                NOT FOR OFFICIAL USE — These results are provisional.
              </span>
            </div>
          )}

          {/* Visible table on the page */}
          {filteredResults.length === 0 ? (
            <p>No results available for the selected level and semester.</p>
          ) : (
            <table
              className="results-table"
              style={{ width: "100%", borderCollapse: "collapse" }}
            >
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Level</th>
                  <th>Semester</th>
                  <th>Course</th>
                  <th style={{ width: 80 }}>Units</th>
                  <th style={{ width: 80 }}>Score</th>
                  <th style={{ width: 80 }}>Grade</th>
                  <th style={{ width: 80 }}>Points</th>
                  <th style={{ width: 200 }}>Date Entered</th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.map((r, idx) => (
                  <tr key={r.ResultID ?? `${r.CourseID}-${idx}`}>
                    <td>{idx + 1}</td>
                    <td>{r.CourseLevel}</td>
                    <td>{r.CourseSemester}</td>
                    <td>
                      <strong>{r.CourseCode ?? ""}</strong> —{" "}
                      {r.CourseTitle ?? ""}
                    </td>
                    <td style={{ textAlign: "center" }}>{r.Units || 0}</td>
                    <td style={{ textAlign: "center" }}>
                      {r.Score == null ? "N/A" : r.Score}
                    </td>
                    <td style={{ textAlign: "center" }}>{r.Grade}</td>
                    <td style={{ textAlign: "center" }}>{r.Points}</td>
                    <td>
                      {r.EnteredAt
                        ? new Date(r.EnteredAt).toLocaleString()
                        : "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ marginTop: 12 }}>
            <div>
              <strong>Semester Units:</strong> {semesterTotalUnits || 0}
            </div>
            <div>
              <strong>Semester GPA:</strong>{" "}
              {Number.isFinite(semesterGPA) ? semesterGPA.toFixed(2) : "0.00"}
            </div>
            <div style={{ marginTop: 6 }}>
              <strong>Cumulative Units:</strong> {cgpaTotalUnits || 0}
            </div>
            <div>
              <strong>CGPA:</strong>{" "}
              {Number.isFinite(cgpa) ? cgpa.toFixed(2) : "0.00"}
            </div>
          </div>

          {/* Hidden print copy */}
          <div ref={printRef} style={{ display: "none" }}>
            <div className="print-area">
              <div className="watermark">PROVISIONAL RESULT</div>

              <div className="print-header">
                <div>
                  <div className="print-title">
                    <h2>LAGOS UNIVERSITY TEACHING HOSPITAL (LUTH)</h2>
                  </div>
                  <div className="print-subtitle">
                    <h3>STUDENT RESULTS SHEET</h3>
                  </div>
                </div>

                <div style={{ textAlign: "right" }}>
                  <div>
                    <strong>Matric No:</strong> {matricNo || "-"}
                  </div>
                  <div>
                    <strong>Name:</strong>{" "}
                    {studentInfo?.FirstName || studentInfo?.fullName || "-"}
                  </div>
                </div>
              </div>

              <div className="print-meta">
                <div className="meta-box">
                  <strong>Level:</strong> {selectedLevel || "All Levels"}
                </div>
                <div className="meta-box">
                  <strong>Semester:</strong>{" "}
                  {selectedSemester || "All Semesters"}
                </div>
                <div className="meta-box">
                  <strong>Status:</strong> Provisional
                </div>
              </div>

              {isProvisional && (
                <div className="provisional-banner">
                  NOT FOR OFFICIAL USE — These results are provisional.
                </div>
              )}

              {filteredResults.length === 0 ? (
                <p>No results available for the selected level and semester.</p>
              ) : (
                <>
                  <table className="results-table">
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}>#</th>
                        <th>Level</th>
                        <th>Semester</th>
                        <th>Course</th>
                        <th style={{ width: 80 }}>Units</th>
                        <th style={{ width: 80 }}>Score</th>
                        <th style={{ width: 80 }}>Grade</th>
                        <th style={{ width: 80 }}>Points</th>
                        <th style={{ width: 200 }}>Date Entered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredResults.map((r, idx) => (
                        <tr key={r.ResultID ?? `${r.CourseID}-${idx}`}>
                          <td>{idx + 1}</td>
                          <td>{r.CourseLevel}</td>
                          <td>{r.CourseSemester}</td>
                          <td>
                            <strong>{r.CourseCode ?? ""}</strong> —{" "}
                            {r.CourseTitle ?? ""}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {r.Units || 0}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {r.Score == null ? "N/A" : r.Score}
                          </td>
                          <td style={{ textAlign: "center" }}>{r.Grade}</td>
                          <td style={{ textAlign: "center" }}>{r.Points}</td>
                          <td>
                            {r.EnteredAt
                              ? new Date(r.EnteredAt).toLocaleString()
                              : "N/A"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="footer-summary">
                    <div>
                      <strong>Semester Units:</strong> {semesterTotalUnits || 0}
                    </div>
                    <div>
                      <strong>Semester GPA:</strong>{" "}
                      {Number.isFinite(semesterGPA)
                        ? semesterGPA.toFixed(2)
                        : "0.00"}
                    </div>
                    <div>
                      <strong>Cumulative Units:</strong> {cgpaTotalUnits || 0}
                    </div>
                    <div>
                      <strong>CGPA:</strong>{" "}
                      {Number.isFinite(cgpa) ? cgpa.toFixed(2) : "0.00"}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
